import { FC, memo, useEffect, useState } from 'react';
import { VariableSizeList } from 'react-window';
import { isArray, isEqual } from '@lhb/func';
import { allData, collecting } from '../../ts-config';
import AutoSizer from 'react-virtualized-auto-sizer';
import ListItem from './ListItem';

/**
 * @Description 场地列表
 */
const List: FC<{
  mapIns,
  lastIndexRef, // 记录关闭时的索引
  listRef, // 列表Ref
  rankSort, // 前xx名
  openList, // 是否展开
  itemData, // 选中的商圈
  poiData,
  distanceSort, // 是否是距离排序
  listData, // 列表数据
  setItemData,
  setShowBack, // 是否显示滚动到顶部
  collectList,
  cityProvinceInfo, // 左上角城市信息
  isBatchCollect,
  batchCollectIds, // 批量收藏的开店区域ids
  setBatchCollectIds, // 设置批量收藏的开店区域ids
  emitVariableListData, // 向外部同步虚拟列表的数据
}> = ({
  mapIns,
  lastIndexRef, // 记录关闭时的索引
  listRef, // 列表Ref
  rankSort, // 前xx名
  openList, // 是否展开
  itemData, // 选中的商圈
  poiData,
  distanceSort, // 是否是距离排序
  listData, // 列表数据
  setItemData,
  setShowBack, // 是否显示滚动到顶部
  collectList,
  cityProvinceInfo, // 左上角城市信息
  isBatchCollect,
  batchCollectIds, // 批量收藏的开店区域ids
  setBatchCollectIds, // 设置批量收藏的开店区域ids
  emitVariableListData, // 向外部同步虚拟列表的数据
}) => {
  const [variableListData, setVariableListData] = useState<any[]>([]);
  const [refLoaded, setRefLoaded] = useState(false);

  // 拿到listRef之后，监听itemData、variableListData、openList变化
  useEffect(() => {
    if (!openList) {
      setRefLoaded(false);
      return;
    };
    const { id, isBatchCollectChange } = itemData || {};
    // 列表数据变化时 | 收起后又展开时，滚动到对应位置
    if (!id && refLoaded && variableListData?.length && listRef.current) {
      // 重置尺寸
      // listRef.current.resetAfterIndex(visibleStartIndexRef.current);
      // visibleStartIndexRef.current = 0;
      // lastIndexRef.current = 0;
      const len = variableListData.length;
      for (let i = 0; i < len; i++) {
        // if (variableListData[i]?.isFrontEndExpanded) {
        variableListData[i].isFrontEndExpanded = false;
        listRef.current.resetAfterIndex(i);
        // }
      }
      // 进入批量收藏状态时 || 特殊标记时
      if (isBatchCollect || isBatchCollectChange) return;
      // 没有itemData而且弹窗关闭前的索引不为0，滚动之前的位置
      listRef.current.scrollToItem(lastIndexRef.current || 0, 'start');
      return;
    }
    // 列表打开状态时设置itemData后，滚动到对应的位置
    const index = variableListData.findIndex((item: any) => item.id === id);
    if (index > -1 && listRef.current) {
      const target = variableListData[index];
      target && (target.isFrontEndExpanded = true);

      // 将其余项收起
      const len = variableListData.length;
      for (let i = 0; i < len; i++) {
        if (i !== index && variableListData[i]?.isFrontEndExpanded) {
          variableListData[i].isFrontEndExpanded = false;
          listRef.current.resetAfterIndex(i);
        }
      }
      lastIndexRef.current = index;
      listRef.current.scrollToItem(index, 'start');
      listRef.current.resetAfterIndex(index);
    }
  }, [itemData, variableListData, refLoaded, openList]);

  useEffect(() => {
    if (!(isArray(listData) && listData?.length)) {
      setVariableListData([]);
      emitVariableListData && emitVariableListData([]);
    };
    const { value } = rankSort;
    // 选择排名时
    const targetListData = listData?.filter((item) => {
      if (value === collecting) { // 被收藏的数据
        return collectList.includes(item?.id);
      } else if (value === allData) { // 全部数据
        return true;
      } else {
        return item.rank < value;
      }
    });
    if (poiData) { // 搜索了地址
      const { longitude: lng, latitude: lat } = poiData;
      const targetData = targetListData.map((item: any) => {
        let distance = '';
        if (+item.lng && +item.lat && +lng && +lat) {
          distance = window.AMap.GeometryUtil.distance([item.lng, item.lat], [lng, lat]);
        }
        return {
          ...item,
          distance,
          // isFrontEndExpanded: false,
          isFrontEndExpanded: !!variableListData.find((targetItem: any) => targetItem.id === item.id)?.isFrontEndExpanded
        };
      });
      setVariableListData(targetData);
      emitVariableListData && emitVariableListData(targetData);
      if (distanceSort) { // 选择了排序
        const sortData = targetData.sort((a, b) => {
          const targetA = a?.distance;
          const targetB = b?.distance;
          return targetA - targetB;
        });
        sortChange(sortData);
        setVariableListData(sortData);
      } else {
        sortChange(targetData);
      }
      return;
    }
    setVariableListData(targetListData.map((item: any) => ({ ...item, isFrontEndExpanded: false })));
    emitVariableListData && emitVariableListData(targetListData);
  }, [listData, distanceSort, poiData, rankSort, collectList]);

  useEffect(() => {
    // 切换排名时，清空原本的状态及高度
    if (variableListData?.[lastIndexRef.current] && listRef.current) {
      variableListData[lastIndexRef.current].isFrontEndExpanded = false;
      listRef.current.resetAfterIndex(lastIndexRef.current);
    }
    lastIndexRef.current = 0;
    // 清空选中
    setItemData({
      visible: false, // 是否显示详情
      id: null,
      detail: null, // 存放详情相关字段
      isFirst: false
    });
    backTop();
  }, [rankSort?.key]);

  // 在收藏tab下，取消收藏/详情页取消收藏后触发(展开或者打开详情的操作都会setItemData来记录当前项，在取消收藏后需要把itemData置为空)
  useEffect(() => {
    if (rankSort.value === collecting) {
    // 清空选中
      setItemData({
        visible: false, // 是否显示详情
        id: null,
        detail: null, // 存放详情相关字段
        isFirst: false
      });
    }
  }, [collectList, rankSort.value]);

  // 进入/退出批量收藏状态时 | 列表项展开后，地图选中或者列表项复选框选中，收起展开项
  useEffect(() => {
    setItemData({
      visible: false, // 是否显示详情
      id: null,
      detail: null, // 存放详情相关字段
      isFirst: false,
      isBatchCollectChange: true, // 特殊字段，只用在这里，其他地方set时都没这个字段
    });
  }, [isBatchCollect, batchCollectIds]);

  // 每项的高度
  const getItemSize = (index: number) => {
    // 展开的高度
    const expandedHeight = 400;
    // 收起高度
    const notExpandedHeight = 50;
    return variableListData[index]?.isFrontEndExpanded ? expandedHeight : notExpandedHeight;
  };

  // const isItemLoaded = (index) => {
  //   const target = listData[index] || {};
  //   // console.log(`isItemLoaded`, index);
  //   return Object.entries(target)?.length > 0;
  // };
  const [placeDetail, setPlaceDetail] = useState<{
    id?: number, // 场地id
    address?: string, // 场地地址
    spotList?: any[], // 场地下点位列表
    [p: string]: any
  }>({});

  const ItemRenderer = ({ index, style, data }) => {
    return (
      <ListItem
        mapIns={mapIns}
        listRef={listRef}
        index={index}
        style={style}
        item={data[index]}
        listData={data}
        setItemData={setItemData}
        needFitView={true}
        cityProvinceInfo={cityProvinceInfo}
        isBatchCollect={isBatchCollect}
        batchCollectIds={batchCollectIds}
        setBatchCollectIds={setBatchCollectIds}
        placeDetail={placeDetail}
        setPlaceDetail={setPlaceDetail}
      />

    );
  };
  // 市场评分排序/距离排序切换
  const sortChange = (sortData: any) => {
    // 是否有展开项
    const expandedItem = sortData.find((item: any) => item.isFrontEndExpanded);
    if (expandedItem) {
      if (variableListData?.[lastIndexRef.current] && listRef.current) {
        variableListData[lastIndexRef.current].isFrontEndExpanded = false;
        listRef.current.resetAfterIndex(lastIndexRef.current);
      }
    }
    lastIndexRef.current = 0;
    // 没有展开项时才将itemData置为空
    setItemData({
      visible: !!expandedItem, // 是否显示详情
      id: expandedItem?.id || null,
      detail: expandedItem || null, // 存放详情相关字段
      isFirst: false
    });
  };

  // const ItemRenderer = memo(
  //   props => {
  //     const { index, style, data } = props as any;
  //     // return <div style={style}>Row {index}</div>;
  //     return (
  //       <ListItem
  //         mapIns={mapIns}
  //         listRef={listRef}
  //         index={index}
  //         style={style}
  //         item={data[index]}
  //         listData={data}
  //         firstLevelCategory={firstLevelCategory}
  //         setDrawerData={setDrawerData}
  //         setItemData={setItemData}
  //       />
  //     );
  //   },
  //   areEqual
  // );
  const onItemsRendered = ({ visibleStartIndex }) => {
    // 组件重新渲染时，visibleStartIndex是0，这里只记录重新渲染之前的索引
    if (visibleStartIndex > 0 && lastIndexRef.current > 0) {
      // visibleStartIndexRef.current = visibleStartIndex; // 记录当前可视区域第一项的索引
      lastIndexRef.current = visibleStartIndex;
    }
    // 确保在useEffect能拿到ref值
    setRefLoaded(true);
  };
  // 监听虚拟列表滚动
  const onScroll = ({
    // scrollDirection, // 'forward' 或 'backward',
    scrollOffset, // 当前的滚动偏移量
    // scrollUpdateWasRequested // 滚动是否是由用户操作引起的
  }) => {
    setShowBack(scrollOffset > 500);
  };
  const backTop = () => {
    listRef.current && listRef.current.scrollToItem(0, 'start');
  };
  // 这段注释代码不要删除
  // const onItemsRenderedWrapper = ({
  //   onItemsRendered,
  //   data
  // }) => {
  //   // ...rest
  //   return ({
  //     visibleStartIndex,
  //     visibleStopIndex,
  //     // overscanStartIndex,
  //     // overscanStopIndex
  //   }) => {
  //     onItemsRendered({
  //       // overscanStartIndex, // 被预渲染的第一项的索引
  //       // overscanStopIndex, // 被预渲染的最后一项的索引
  //       visibleStartIndex, // 可视区域的第一项的索引
  //       visibleStopIndex // 可视区域的最后一项的索引
  //     });

  //     // const startItem = data[visibleStartIndex];
  //     // const endItem = data[visibleStopIndex];
  //     // dataRef.current.page
  //     // 向上检查一页 向下检查一页
  //     // if (visibleStartIndex > 0) {
  //     //   dataRef.current.page =
  //     // }
  //   };
  // };

  return <>
    {
    // https://www.npmjs.com/package/react-virtualized-auto-sizer

      <AutoSizer>
        {({ height, width }) => (
        // https://www.npmjs.com/package/react-window-infinite-loader
        // <InfiniteLoader
        //   isItemLoaded={isItemLoaded}
        //   itemCount={total}
        //   loadMoreItems={loadData}
        // >
        // {({ onItemsRendered, ref }) => (
          <VariableSizeList
            // ref={(list) => {
            //   // 将List的ref分别传递给InfiniteLoader和外部ref
            //   // 这个方法必须正确地设置InfiniteLoader的ref
            //   ref(list);
            //   listRef.current = list;
            // }}
            ref={listRef}
            // 列表中项目的总数
            itemCount={variableListData?.length}
            // 列表可视区域高度(减去(排序 + 排名所占的高度)
            height={height -
            // 非市场容量下，（开启了批量收藏 ？ ：顶部 ）
               (isBatchCollect ? (47 + 56) : 88) || 0}
            width={width}
            // 每一项的高度
            // itemSize={getItemSize}
            // itemSize必须是一个函数
            itemSize={getItemSize}
            itemData={variableListData}
            // onItemsRendered={onItemsRenderedWrapper({ onItemsRendered, data: listData })}
            onItemsRendered={onItemsRendered}
            onScroll={onScroll} // 监听滚动事件
          >
            {ItemRenderer}
          </VariableSizeList>
        )}
        {/* </InfiniteLoader> */}
        {/* )} */}
      </AutoSizer>
    }
  </>;
};

const areRowsEqual = (prevProps, nextProps) => {
  const { listData: lastData, poiData: prevPoiData, collectList: prevCollectList } = prevProps;
  const { listData: nextData, poiData: nextPoiData, collectList: nextCollectList } = nextProps;
  const lastDataIds = lastData.map((item) => item.id);
  const nextDataIds = nextData.map((item) => item.id);
  return isEqual(lastDataIds, nextDataIds) &&
  prevProps?.itemData?.id === nextProps?.itemData?.id &&
  prevProps?.drawerData?.id === nextProps?.drawerData?.id &&
  prevProps?.openList === nextProps?.openList &&
  isEqual(prevPoiData, nextPoiData) &&
  prevProps?.distanceSort === nextProps?.distanceSort &&
  prevProps?.rankSort?.key === nextProps?.rankSort?.key &&
  isEqual(prevCollectList, nextCollectList) &&
  prevProps.isBatchCollect === nextProps.isBatchCollect &&
  isEqual(prevProps?.batchCollectIds, nextProps?.batchCollectIds);
};

export default memo(List, areRowsEqual);
